import { useRef, useState } from "react";

import { isAPIError } from "@/core/services";
import { readBusinessError } from "@/core/utils";
import { useFieldErrors } from "@/ds";
import { criarFichaFinanceira, incluirFichaAnexos } from "@/modules/financeiro-fichas";
import type { FichaFinanceiraCriada, IncluirFichaAnexoPayload } from "@/modules/financeiro-fichas";
import {
  FICHA_FORM_REGRAS,
  FICHA_FORM_VAZIO,
  montarPayloadFicha,
  validarCampoFicha,
} from "./ficha-previa-form";
import type { FichaCampoEditavel, FichaFormValues } from "./ficha-previa-form";
import { novaChave } from "@/shared/hooks";
import { usePreviaFichaFinanceira } from "./use-fichas-financeiras";

/** Falha ao gravar a ficha. */
export const FICHA_SALVAR_ERROR_MESSAGE = "Não foi possível salvar a ficha. Tente novamente.";

/** Anexo escolhido na prévia: fica só em memória até o Salvar. */
export interface AnexoPendente extends IncluirFichaAnexoPayload {
  id: string;
}

/** A ficha já foi gravada, mas os anexos não subiram; Salvar de novo não cria outra ficha e reenvia o lote. */
export const ANEXOS_SALVAR_ERROR_MESSAGE =
  "Ficha salva, mas não foi possível anexar os arquivos. Clique em Salvar para tentar novamente.";

export type ResultadoSalvarFicha = "salva" | "invalida" | "erro" | "ocupado";

/**
 * Geração da ficha (HU 2684): prévia do banco, valores dos campos editáveis, validação em tela e
 * gravação. Só consulta a prévia com `ativo`; sem requisição enquanto houver campo inválido.
 */
export function useGerarFicha(obraId: string, ativo: boolean) {
  const previa = usePreviaFichaFinanceira(obraId, ativo);
  const [values, setValues] = useState<FichaFormValues>(FICHA_FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [salva, setSalva] = useState<FichaFinanceiraCriada | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [anexos, setAnexos] = useState<AnexoPendente[]>([]);
  // Ficha já gravada nesta ação de Salvar, à espera dos anexos: a retentativa não cria outra.
  const fichaCriada = useRef<FichaFinanceiraCriada | null>(null);
  // Uma chave por tentativa: a retentativa depois de falha reaproveita a mesma, para o bff não
  // criar a ficha duas vezes; salvar com sucesso a descarta.
  const chaveIdempotencia = useRef<string | null>(null);
  const { errors, setError, clearError, validate } = useFieldErrors<FichaCampoEditavel>();

  function alterar(campo: FichaCampoEditavel, valor: string) {
    // Payload diferente é outra tentativa: não pode sair com a chave da anterior.
    chaveIdempotencia.current = null;
    setValues((atual) => ({ ...atual, [campo]: valor }));

    const erro = validarCampoFicha(valor);
    if (erro) setError(campo, erro);
    else clearError(campo);
  }

  function adicionarAnexo(payload: IncluirFichaAnexoPayload) {
    setAnexos((atual) => [...atual, { ...payload, id: novaChave() }]);
  }

  function removerAnexo(id: string) {
    setAnexos((atual) => atual.filter((anexo) => anexo.id !== id));
  }

  async function salvar(): Promise<ResultadoSalvarFicha> {
    if (salvando) return "ocupado";
    if (!validate(FICHA_FORM_REGRAS, values)) return "invalida";

    setSalvando(true);
    setErroSalvar(null);
    chaveIdempotencia.current ??= novaChave();
    try {
      if (!fichaCriada.current) {
        fichaCriada.current = await criarFichaFinanceira(obraId, montarPayloadFicha(values), {
          idempotencyKey: chaveIdempotencia.current,
        });
        chaveIdempotencia.current = null;
      }
      if (anexos.length > 0) {
        try {
          // Um único envio com todos os arquivos: o servidor grava o lote inteiro ou nada.
          await incluirFichaAnexos(
            obraId,
            fichaCriada.current.id,
            anexos.map(({ identificacao, arquivo }) => ({ identificacao, arquivo })),
          );
          setAnexos([]);
        } catch {
          setErroSalvar(ANEXOS_SALVAR_ERROR_MESSAGE);
          return "erro";
        }
      }
      setSalva(fichaCriada.current);
      fichaCriada.current = null;
      return "salva";
    } catch (error) {
      const negocio = isAPIError(error) && error.status === 422 ? readBusinessError(error) : null;
      setErroSalvar(negocio?.message || FICHA_SALVAR_ERROR_MESSAGE);
      // Regra de negócio recusou: a próxima tentativa já é outra, com outra chave.
      if (negocio) chaveIdempotencia.current = null;
      return "erro";
    } finally {
      setSalvando(false);
    }
  }

  return {
    status: previa.status,
    errorMessage: previa.errorMessage,
    reload: previa.reload,
    previaBloqueio: previa.businessMessage,
    previa: previa.data,
    values,
    errors,
    salvando,
    salva,
    erroSalvar,
    anexos,
    adicionarAnexo,
    removerAnexo,
    alterar,
    salvar,
  };
}
